<template>
	<Loader v-if="loading" />

	<div class="xpage-content-page">
		<!-- why do we have to do this in the body components -->
		<Page>
			<!-- why isn't this a shared breadcrumb component -->
			<div class="xpage-breadcrumbs">
				<router-link to="/" class="icon-home"></router-link>
				<span>></span>
				<router-link :to="`/batches`">Batches</router-link>
			</div>

			<div class="page-content">
				<BatchHeader :batch="batch" />

				<div class="table-wrapper">
					<table>
						<thead>
							<tr>
								<th v-for="header in headers" :key="header.key">
									<span
										v-if="header.sortable"
										@click="sortBy(header.key)"
										:class="isActiveSort(header.key)"
									>
										{{ header.label }} {{ getSortArrow(header.key) }}
									</span>
									<span v-else>
										{{ header.label }}
									</span>
								</th>
							</tr>
						</thead>
						<tbody>
							<BatchPayoffRow
								v-for="payoffReference in sortedPayoffReferences"
								:key="payoffReference.payoffId"
								:batchId="batch?.id"
								:payoffReference="payoffReference"
								:isBatchApproved="batch?.isApproved"
								:isProcessing="isProcessing"
								:onBatchUpdateAsync="fetchBatchDetailsAsync"
							/>
						</tbody>
					</table>
				</div>

				<div class="buttons-container">
					<PermissionsWrapper
						:permissionIds="[Permissions.DisbursementBatches]"
						:permissionLevel="PermissionLevels.Update"
					>
						<button :disabled="batch?.isApproved || isProcessing" @click="approveBatchAsync">
							Approve
						</button>
					</PermissionsWrapper>
					<button class="secondary" @click="goBack">Back</button>
				</div>
			</div>
		</Page>
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted, computed } from 'vue';
	import { useRouter, useRoute } from 'vue-router';
	import axios from 'axios';
	import Loader from '@/components/Loader.vue';
	import Page from '@/components/layout/Page.vue';
	import functions from '@/use/functions';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import BatchHeader from './BatchHeader.vue';
	import BatchPayoffRow from './BatchPayoffRow.vue';
	import { BatchDetailsModel, BatchReferenceModel } from '../../models/payoffBatches';

	const route = useRoute();
	const router = useRouter();

	const batch = ref<BatchDetailsModel | null>(null);
	const payoffReferences = ref<BatchReferenceModel[]>([]);

	const loading = ref(false);
	const isProcessing = ref(false);

	const headers = ref([
		{ label: 'In|Out', sortable: false, key: '' },
		{ label: 'Credit Union', sortable: true, key: 'lenderName' },
		{ label: 'Borrower (F/L)', sortable: true, key: 'borrowerName' },
		{ label: 'Scheduled Amount', sortable: true, key: 'disbursementAmountFormatted' },
		{ label: 'Status', sortable: true, key: 'disbursementStatusHumanized' },
		{ label: 'Method', sortable: true, key: 'disbursementMethodHumanized' },
		{ label: 'Reference', sortable: true, key: 'referenceId' },
		{ label: 'Lender Use (LoanId + PayoffId)', sortable: true, key: 'lenderUseOnly' },
		{ label: 'Sequence', sortable: true, key: 'sequenceNumber' }
	]);

	const fetchBatchDetailsAsync = async () => {
		try {
			const response = await axios.get(`/api/payoffbatches/${route.params.batchId}`);

			batch.value = response.data;
			payoffReferences.value = response.data.payoffReferences;
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to get details for payoff batch ${route.params.batchId}. Please try again later.`
			});
		} finally {
			loading.value = false;
		}
	};

	const approveBatchAsync = async () => {
		if (isProcessing.value) {
			return;
		}

		isProcessing.value = true;
		loading.value = true;

		try {
			await axios.post(`/api/payoffbatches/${route.params.batchId}/approve`);
			await fetchBatchDetailsAsync();
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to approve payoff batch ${route.params.batchId}. Please try again later.`
			});
		} finally {
			isProcessing.value = false;
			loading.value = false;
		}
	};

	const goBack = () => router.go(-1);

	const sortKey = ref('');
	const sortOrder = ref(1);

	const sortedPayoffReferences = computed(() => {
		if (!sortKey.value) {
			return payoffReferences.value; // no sorting, return original order
		}

		return [...payoffReferences.value].sort((a, b) => {
			let valA = a.payoff ? a.payoff[sortKey.value] : null;
			let valB = b.payoff ? b.payoff[sortKey.value] : null;

			if (typeof valA === 'string') {
				valA = valA.toLowerCase();
			}

			if (typeof valB === 'string') {
				valB = valB.toLowerCase();
			}

			if (valA < valB) {
				return -1 * sortOrder.value;
			} else if (valA > valB) {
				return 1 * sortOrder.value;
			}

			return 0;
		});
	});

	const sortBy = (key: string) => {
		if (sortKey.value === key) {
			if (sortOrder.value === 1) {
				sortOrder.value = -1; // second click, descending
			} else if (sortOrder.value === -1) {
				sortKey.value = ''; // third click, reset sort
				sortOrder.value = 1; // default sort order for the next click
			}
		} else {
			sortKey.value = key;
			sortOrder.value = 1; // first click, ascending
		}
	};

	const getSortArrow = (key: string) => {
		if (sortKey.value === key) {
			return sortOrder.value === 1 ? '▲' : sortOrder.value === -1 ? '▼' : ' ';
		}
		return '';
	};

	const isActiveSort = (key: string) => (sortKey.value === key ? 'active-sort' : '');

	onMounted(async () => {
		loading.value = true;

		try {
			await fetchBatchDetailsAsync();
		} catch (error) {
			console.error(error);
		} finally {
			loading.value = false;
		}
	});
</script>

<!-- todo: create a standard responsive data table component -->
<!-- todo: create standard button components -->

<style scoped>
	.page-content {
		padding: 0 20px;
	}

	.table-wrapper {
		overflow-x: auto;
		width: 100%;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 14px;
		margin-top: 20px;
		text-align: center;
		border: 1px solid #ccc;
	}

	table tr:hover {
		background-color: #f2f2f2;
		transition: background-color 0.2s;
	}

	thead th {
		background-color: #f2f2f2;
		font-weight: 800;
		padding: 10px 5px;
		border-bottom: 1px solid #ccc;
		cursor: pointer;
		user-select: none;
	}

	th:hover {
		background-color: #ddd;
	}

	tr:nth-child(even) {
		background-color: #f9f9f9;
	}

	td,
	th {
		text-align: center;
	}

	td:nth-child(1) {
		width: 40px;
	}

	.buttons-container {
		display: flex;
		justify-content: flex-end;
		margin-top: 20px;
		gap: 10px;
	}

	button {
		padding: 8px 20px;
		font-size: 14px;
		font-weight: bold;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		transition: background-color 0.2s, color 0.2s, opacity 0.2s;
	}

	button:hover {
		background-color: #333;
		cursor: pointer;
	}

	button.secondary {
		color: black;
		background-color: white;
		box-shadow: inset 0 0 0 2px #ccc;
	}

	button.secondary:hover {
		color: white;
		background-color: black;
		box-shadow: inset 0 0 0 2px black;
	}

	button:disabled {
		background-color: #ccc;
		color: #666;
		cursor: not-allowed;
		opacity: 0.7;
	}

	button.secondary:disabled {
		background-color: #ccc;
		color: #999;
		box-shadow: inset 0 0 0 2px #ccc;
	}

	/* copied xpage classes for consistency */

	.xpage-breadcrumbs {
		margin-bottom: 20px;
		display: flex;
		align-items: center;
	}

	.xpage-breadcrumbs span {
		margin: 0 5px;
	}

	.xpage-content-page {
		padding-left: 20px;
	}
</style>
